import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router';
import styled from 'styled-components';

import { BodyL } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_CLUBS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FootballTabs } from '#src/LicensedPartners/PartnersList/FootballTabs/index.tsx';
import { Header } from '#src/LicensedPartners/PartnersList/Header/index.tsx';
import { Item } from '#src/LicensedPartners/PartnersList/Item/index.tsx';
import { StickySectionTitle } from '#src/LicensedPartners/PartnersList/StickySectionTitle/index.tsx';

import type { FootballClubs_club } from './__generated__/index.graphql.ts';

const CountryFlag = styled.img`
  height: 20px;
  border-radius: 2px;
`;
const ClubsPerCountry = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  padding-top: var(--double-unit);
  @media ${laptopAndAbove} {
    grid-template-columns: repeat(4, 1fr);
    margin-left: 270px;
    margin-top: -100px;
    padding-top: 0;
  }
  margin-bottom: var(--quadruple-unit);
  gap: var(--double-unit);
`;

type Props = {
  clubs: Nullable<FootballClubs_club[]>;
};

export const FootballClubs = ({ clubs }: Props) => {
  const [searchValue, setSearchValue] = useState('');

  const { formatMessage } = useIntl();

  const displayedClubs = useMemo(() => {
    // Filtering out russian clubs because of the war in Ukraine
    const clubsWithoutRussians = clubs?.filter(
      club => club.country.slug !== 'ru'
    );
    let filteredClubs;
    if (searchValue.trim()) {
      filteredClubs = clubsWithoutRussians?.filter(
        club =>
          club.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          club.code?.toLowerCase().includes(searchValue.toLowerCase())
      );
    } else {
      filteredClubs = clubsWithoutRussians;
    }
    filteredClubs = filteredClubs?.map(club => {
      if (club.slug === 'monaco-monaco') {
        // override AS Monaco country to have it listed under France
        // for easier searching
        return {
          ...club,
          country: {
            name: 'France',
            flagUrl:
              'https://frontend-assets.sorare.com/flags-iso/flat/32/FR.png',
            slug: 'fr',
          },
        };
      }
      return club;
    });
    const grouped =
      filteredClubs &&
      groupBy(filteredClubs, c => c.country.name || 'no-country');
    return Object.entries(grouped || {})
      .sort()
      .map(([, clubsList = []]) =>
        clubsList.sort((a, b) => a.name.localeCompare(b.name))
      );
  }, [clubs, searchValue]);

  return (
    <>
      <Header
        onSearchInputChange={setSearchValue}
        searchPlaceholder={formatMessage({
          id: 'LicensedClubs.footballSearch',
          defaultMessage: 'Search for a football team',
        })}
      >
        <FootballTabs />
      </Header>
      {displayedClubs.map(clubsList => {
        const { country } = clubsList[0];
        if (!country) {
          return null;
        }
        return (
          <Fragment key={country.slug}>
            <StickySectionTitle href={`#${country.slug}`}>
              <CountryFlag src={country.flagUrl || ''} alt="" />
              <BodyL>{country.name}</BodyL>
            </StickySectionTitle>
            <ClubsPerCountry id={country.slug}>
              {clubsList.map(club => (
                <Item
                  key={club.slug}
                  name={club.name}
                  to={generatePath(FOOTBALL_CLUBS_SLUG, {
                    slug: club.slug,
                  })}
                  logoUrl={club.pictureUrl || ''}
                />
              ))}
            </ClubsPerCountry>
          </Fragment>
        );
      })}
    </>
  );
};

FootballClubs.fragments = {
  club: gql`
    fragment FootballClubs_club on Club {
      slug
      name
      code
      pictureUrl
      country {
        slug
        name
        flagUrl(size: 32)
      }
    }
  ` as TypedDocumentNode<FootballClubs_club>,
};
